import type { ChangeEvent, FC } from "react";

import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { selectParsingError } from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import managementTrackingService from "app/pages/sales-intelligence/modules/management/services/tracking/managementTrackingService";
import { resolveSignalsApiCustomAttributes } from "app/pages/sales-intelligence/modules/management/services/tracking/utils";
import { UploadCSV } from "app/pages/sales-intelligence/sub-modules/management/components/LoadList/components/UploadCSV";
import type { ErrorTexts, ParsingErrors } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { SignalsApiStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/types";
import {
SUBSCRIBE_DOMAINS_CSV_LIMIT,
SUBSCRIBE_DOMAINS_HINT_WIDTH,
} from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/constants";

import { UploadInstructions } from "../../components/UploadInstructions/UploadInstructions";
import { useUploadDomainsFile } from "../../mutations/useUploadDomainsFile";
import {
    SUBSCRIBE_DOMAINS_COL_NUMBER_ERROR,
    SUBSCRIBE_DOMAINS_FORMAT_ERROR,
    SUBSCRIBE_DOMAINS_NOT_A_DOMAINS_ERROR,
    SUBSCRIBE_DOMAINS_OVER_LIMIT_ERROR,
} from "../../translations/upload.texts";
import { SubscribeDomainsUploadMode } from "../../types/enum";

import { StyledSubscribeDomainsEmptyView } from "./styles";

interface SubscribeDomainsEmptyViewProps {
    data?: SignalsApiStatusResponse;
}

export const SubscribeDomainsEmptyView: FC<SubscribeDomainsEmptyViewProps> = ({ data }) => {
    const uploadFile = useUploadDomainsFile();

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
        managementTrackingService.trackSignalApiClickEvent("upload file", resolveSignalsApiCustomAttributes(data));
        const csvFile = event?.target?.files?.[0];

        if (csvFile) {
            uploadFile.mutate({ file: csvFile, mode: SubscribeDomainsUploadMode.Append });
        }
    };

    const errorTexts: ErrorTexts = {
        isOverTheLimit: translate(SUBSCRIBE_DOMAINS_OVER_LIMIT_ERROR, { limit: SUBSCRIBE_DOMAINS_CSV_LIMIT }),
        notADomains: translate(SUBSCRIBE_DOMAINS_NOT_A_DOMAINS_ERROR),
        columnsNumber: translate(SUBSCRIBE_DOMAINS_COL_NUMBER_ERROR),
        format: translate(SUBSCRIBE_DOMAINS_FORMAT_ERROR),
    };

    const parsingErrors: ParsingErrors = useSelector(selectParsingError);

    return (
        <StyledSubscribeDomainsEmptyView>
            <UploadCSV
                handleFileChange={handleFileChange}
                errorTexts={errorTexts}
                data-automation={"upload-subscribe-domains"}
                isLoading={uploadFile.isPending}
                hasParsingError={false}
                parsingErrors={parsingErrors}
                className="domains-limit"
                title={null}
                isReplace={false}
                customInstructions={<UploadInstructions />}
                hintWidth={SUBSCRIBE_DOMAINS_HINT_WIDTH}
                hasHint={false}
            />
        </StyledSubscribeDomainsEmptyView>
    );
};
